import { jsPDF } from 'jspdf';
import { Message } from '@/types';

/**
 * Format conversation as plain text for clipboard copy.
 * Each message is clearly labeled with the sender role.
 */
export const formatConversationAsText = (
    messages: Message[],
    assistantName: string
): string => {
    return messages
        .map((msg) => {
            const sender = msg.role === 'user' ? 'You' : assistantName;
            const time = new Date(msg.timestamp).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
            });
            return `[${sender}] (${time})\n${msg.content}`;
        })
        .join('\n\n---\n\n');
};

/**
 * Copy the entire conversation to clipboard as formatted text.
 */
export const copyConversationToClipboard = async (
    messages: Message[],
    assistantName: string
): Promise<boolean> => {
    try {
        const text = formatConversationAsText(messages, assistantName);
        await navigator.clipboard.writeText(text);
        return true;
    } catch (err) {
        console.error('Failed to copy conversation:', err);
        return false;
    }
};

/**
 * Strip markdown formatting from text for PDF rendering.
 */
const stripMarkdown = (text: string): string => {
    return text
        // Remove headers
        .replace(/^#{1,6}\s+/gm, '')
        // Remove bold/italic
        .replace(/\*\*(.+?)\*\*/g, '$1')
        .replace(/\*(.+?)\*/g, '$1')
        .replace(/__(.+?)__/g, '$1')
        .replace(/_(.+?)_/g, '$1')
        // Remove inline code
        .replace(/`(.+?)`/g, '$1')
        // Remove links, keep text
        .replace(/\[(.+?)\]\(.+?\)/g, '$1')
        // Remove images
        .replace(/!\[.*?\]\(.+?\)/g, '')
        // Remove blockquotes
        .replace(/^>\s+/gm, '')
        // Clean up bullet points
        .replace(/^[-*+]\s+/gm, '  - ')
        // Clean up numbered lists
        .replace(/^\d+\.\s+/gm, (match) => '  ' + match);
};

/**
 * Download the conversation as a styled PDF.
 */
export const downloadConversationAsPDF = (
    messages: Message[],
    assistantName: string
): void => {
    const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;
    let y = margin;

    // Colors
    const userBgColor: [number, number, number] = [59, 130, 246]; // Blue
    const assistantBgColor: [number, number, number] = [55, 65, 81]; // Gray
    const userTextColor: [number, number, number] = [255, 255, 255];
    const assistantTextColor: [number, number, number] = [229, 231, 235];
    const labelColor: [number, number, number] = [156, 163, 175];
    const headerColor: [number, number, number] = [34, 211, 238]; // Cyan

    // Title
    doc.setFontSize(18);
    doc.setTextColor(...headerColor);
    doc.text(`Conversation with ${assistantName}`, margin, y);
    y += 8;

    // Date
    doc.setFontSize(9);
    doc.setTextColor(...labelColor);
    doc.text(new Date().toLocaleDateString([], {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }), margin, y);
    y += 10;

    // Separator line
    doc.setDrawColor(75, 85, 99);
    doc.setLineWidth(0.3);
    doc.line(margin, y, pageWidth - margin, y);
    y += 8;

    for (const msg of messages) {
        const isUser = msg.role === 'user';
        const sender = isUser ? 'You' : assistantName;
        const time = new Date(msg.timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
        });
        const cleanContent = stripMarkdown(msg.content);

        // Split content into lines that fit the bubble width
        const bubbleWidth = contentWidth - 10;
        doc.setFontSize(10);
        const lines = doc.splitTextToSize(cleanContent, bubbleWidth - 8);

        // Calculate bubble height
        const lineHeight = 4.5;
        const labelHeight = 6;
        const padding = 4;
        const bubbleHeight = labelHeight + lines.length * lineHeight + padding * 2;

        // Check if we need a new page
        if (y + bubbleHeight + 10 > pageHeight - margin) {
            doc.addPage();
            y = margin;
        }

        // Draw sender label
        doc.setFontSize(8);
        doc.setTextColor(...labelColor);
        const labelX = isUser ? pageWidth - margin - doc.getTextWidth(`${sender}  ${time}`) : margin;
        doc.text(`${sender}  ${time}`, labelX, y);
        y += labelHeight;

        // Draw message bubble
        const bgColor = isUser ? userBgColor : assistantBgColor;
        const bubbleX = isUser ? pageWidth - margin - bubbleWidth : margin;

        doc.setFillColor(...bgColor);
        doc.roundedRect(bubbleX, y - 1, bubbleWidth, lines.length * lineHeight + padding * 2, 3, 3, 'F');

        // Draw message text
        const textColor = isUser ? userTextColor : assistantTextColor;
        doc.setFontSize(10);
        doc.setTextColor(...textColor);

        let textY = y + padding + 3;
        for (const line of lines) {
            if (textY > pageHeight - margin) {
                doc.addPage();
                textY = margin + padding;
                // Redraw background on new page for remaining lines
                doc.setFillColor(...bgColor);
            }
            doc.text(line, bubbleX + 4, textY);
            textY += lineHeight;
        }

        y = textY + 4;
    }

    // Footer
    if (y + 15 > pageHeight - margin) {
        doc.addPage();
        y = margin;
    }
    y += 5;
    doc.setDrawColor(75, 85, 99);
    doc.line(margin, y, pageWidth - margin, y);
    y += 6;
    doc.setFontSize(8);
    doc.setTextColor(...labelColor);
    doc.text(`Exported from ${assistantName}`, margin, y);

    // Download
    const filename = `conversation-${assistantName.toLowerCase()}-${Date.now()}.pdf`;
    doc.save(filename);
};
